import { DatePipe } from "@angular/common";
import {
	Component,
	computed,
	inject,
	OnInit,
	signal,
	TemplateRef,
	viewChild,
} from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ConsentType } from "src/app/enums";
import { environment } from "src/environments/environment";
import { ApiService } from "../../../../services/api.service";
import { ProfileService } from "../../../../services/profile/profile.service";
import { UserStorageService } from "../../../../services/user-storage.service";
import { NavbarComponent } from "../../../layout/components/navbar/navbar.component";

interface Consent {
	Type: ConsentType;
	Date: { Iso: string };
	IsDeleted?: boolean;
}

@Component({
	selector: "app-profile-consents",
	templateUrl: "./profile-consents.component.html",
	styleUrls: ["./profile-consents.component.scss"],
	imports: [NavbarComponent, TranslatePipe, DatePipe],
})
export class ProfileConsentsComponent implements OnInit {
	private readonly userStorageService = inject(UserStorageService);
	private readonly apiService = inject(ApiService);
	private readonly profileService = inject(ProfileService);
	private readonly modalService = inject(NgbModal);
	private readonly translate = inject(TranslateService);

	readonly consentRevokeModalTemplate =
		viewChild.required<TemplateRef<any>>("consentRevokeModal");

	// Signals for reactive state management
	readonly #consents = signal<Consent[]>([]);
	readonly #selectedConsent = signal<ConsentType | null>(null);
	readonly #revokeConsentText = signal<string>("");
	readonly #pendingConsentChange = signal<ConsentType | null>(null);

	// Computed properties for better performance
	readonly consent_general = computed(() =>
		this.#consents().find((consent) => consent.Type === ConsentType.General),
	);
	readonly consent_healthscreening = computed(() =>
		this.#consents().find((consent) => consent.Type === ConsentType.Screening),
	);
	readonly consent_wpa = computed(() =>
		this.#consents().find((consent) => consent.Type === ConsentType.WPA),
	);
	readonly consent_healthcheck = computed(() =>
		this.#consents().find(
			(consent) => consent.Type === ConsentType.HealthCheck,
		),
	);
	readonly consent_pulse = computed(() =>
		this.#consents().find((consent) => consent.Type === ConsentType.Pulse),
	);
	readonly consent_whistleblower = computed(() =>
		this.#consents().find(
			(consent) => consent.Type === ConsentType.Whistleblower,
		),
	);
	readonly consent_counselling = computed(() =>
		this.#consents().find(
			(consent) => consent.Type === ConsentType.Counselling,
		),
	);

	// Computed properties for checkbox states that handle pending changes
	readonly consent_general_checked = computed(() => {
		const pendingChange = this.#pendingConsentChange();
		if (pendingChange === ConsentType.General) {
			return false; // Show as unchecked when pending revocation
		}
		return this.consent_general() != undefined;
	});

	readonly consent_healthscreening_checked = computed(() => {
		const pendingChange = this.#pendingConsentChange();
		if (pendingChange === ConsentType.Screening) {
			return false; // Show as unchecked when pending revocation
		}
		return this.consent_healthscreening() != undefined;
	});

	readonly consent_wpa_checked = computed(() => {
		const pendingChange = this.#pendingConsentChange();
		if (pendingChange === ConsentType.WPA) {
			return false; // Show as unchecked when pending revocation
		}
		return this.consent_wpa() != undefined;
	});

	readonly consent_healthcheck_checked = computed(() => {
		const pendingChange = this.#pendingConsentChange();
		if (pendingChange === ConsentType.HealthCheck) {
			return false; // Show as unchecked when pending revocation
		}
		return this.consent_healthcheck() != undefined;
	});

	readonly consent_pulse_checked = computed(() => {
		const pendingChange = this.#pendingConsentChange();
		if (pendingChange === ConsentType.Pulse) {
			return false; // Show as unchecked when pending revocation
		}
		return this.consent_pulse() != undefined;
	});

	readonly consent_whistleblower_checked = computed(() => {
		const pendingChange = this.#pendingConsentChange();
		if (pendingChange === ConsentType.Whistleblower) {
			return false; // Show as unchecked when pending revocation
		}
		return this.consent_whistleblower() != undefined;
	});

	readonly consent_counselling_checked = computed(() => {
		const pendingChange = this.#pendingConsentChange();
		if (pendingChange === ConsentType.Counselling) {
			return false; // Show as unchecked when pending revocation
		}
		return this.consent_counselling() != undefined;
	});

	readonly revokeConsentText = computed(() => this.#revokeConsentText());

	readonly lang = this.userStorageService.getLanguage();

	ngOnInit(): void {
		this.apiService.CheckLogin();
		this.translate.use(this.lang.shortcode);
		this.loadConsents();
	}

	private async loadConsents(): Promise<void> {
		try {
			const consents = await this.profileService.getConsents();
			this.#consents.set(consents);
		} catch (error) {
			console.error("Failed to load consents:", error);
		}
	}

	consentChanged = (consent: Consent | undefined): void => {
		if (!consent) return;

		// Store the consent type that's being changed
		this.#pendingConsentChange.set(consent.Type);
		this.#selectedConsent.set(consent.Type);
		this.#revokeConsentText.set(this.getRevokeConsentText(consent.Type));
		this.showConsentRevokeModal();
	};

	private getRevokeConsentText(consentType: ConsentType): string {
		const isCounselling = consentType === ConsentType.Counselling;
		const translationKey = isCounselling
			? "Revoke_Consent_Counselling_Text"
			: "Revoke_Consent_General_Text";

		return this.translate.instant(translationKey);
	}

	private showConsentRevokeModal(): void {
		this.modalService.open(this.consentRevokeModalTemplate(), {
			ariaLabelledBy: "consent-revoke-modal-title",
			centered: true,
			backdrop: "static",
			keyboard: false,
		});
	}

	revokeConsent = async (): Promise<void> => {
		const selectedConsent = this.#selectedConsent();
		if (!selectedConsent) return;

		try {
			const revoked = await this.profileService.revokeConsent(selectedConsent);

			if (revoked) {
				this.handleSuccessfulRevocation(selectedConsent);
			} else {
				this.revokeConsentCancelled();
			}
		} catch (error) {
			console.error("Failed to revoke consent:", error);
			this.revokeConsentCancelled();
		}
	};

	private handleSuccessfulRevocation(consentType: ConsentType): void {
		// Remove the consent from the list
		const updatedConsents = this.#consents().filter(
			(consent) => consent.Type !== consentType,
		);
		this.#consents.set(updatedConsents);

		// Clear pending change
		this.#pendingConsentChange.set(null);

		// Close the modal
		this.modalService.dismissAll();

		// Redirect if general consent was revoked
		if (consentType === ConsentType.General) {
			window.location.href = environment.UserLoginUrl;
		}
	}

	revokeConsentCancelled = (): void => {
		this.modalService.dismissAll();

		// Clear pending change - this will revert the UI state
		this.#pendingConsentChange.set(null);
	};

	back = (): void => {
		window.history.back();
	};
}
